import { Coins, CheckCircle2, ShieldCheck, CreditCard, ArrowRight, Smartphone, ShieldAlert } from 'lucide-react';
import ContactForm from '../../components/ContactForm';
import SEO from '../../components/SEO';

export default function PrivatfinansieringEn() {
  const anydayFordele = [
    {
      title: 'Flexible installment terms',
      desc: 'Divide payments into manageable monthly slices that suit your personal stable budget.',
      icon: <CreditCard className="h-6 w-6 text-emerald-600" aria-hidden="true" />
    },
    {
      title: 'Up to 24 months',
      desc: 'Choose installment lengths of up to 24 months for maximum financial convenience.',
      icon: <Smartphone className="h-6 w-6 text-emerald-600" aria-hidden="true" />
    },
    {
      title: 'Swift digital approval',
      desc: 'Complete the process online quickly and safely with instant digital verification.',
      icon: <ShieldCheck className="h-6 w-6 text-emerald-600" aria-hidden="true" />
    }
  ];

  const trinGuide = [
    { step: '01', title: 'Choose your machine', desc: 'Select your Danshaker and any desired accessories.' },
    { step: '02', title: 'Apply online', desc: 'Complete the digital application in just a few minutes.' },
    { step: '03', title: 'Instant decision', desc: 'Receive immediate feedback on your credit approval.' },
    { step: '04', title: 'Direct delivery', desc: 'Once signed, production starts and your Danshaker delivers right to your door.' }
  ];

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-950 antialiased">
      <SEO
        title="Private Financing - Flexible Payment Plan"
        description="Finance your Danshaker stable sorting machine with a flexible installment plan from €210/month incl. VAT."
        path="/en/private-financing"
        lang="en"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50 to-slate-50 py-16 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 md:px-8 relative z-10">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            
            {/* Left: Intro */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 border border-emerald-300 px-4 py-1.5 text-xs font-black text-emerald-800 uppercase tracking-wider">
                <Coins className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Private Financing</span>
              </div>
              <h1 className="text-4xl font-black tracking-tight sm:text-6xl text-slate-950">
                Financing for Private Owners
              </h1>
              <p className="text-lg sm:text-xl text-slate-900 font-semibold leading-relaxed">
                Make your daily stable chores easier right away! We collaborate with flexible installment partners like <strong className="text-emerald-700">Anyday</strong>, allowing you to split your machine purchase over up to 24 months.
              </p>
              
              {/* Price Highlight */}
              <div className="inline-block rounded-2xl border-2 border-emerald-300 bg-white p-6 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 left-0 h-2 w-full bg-emerald-500" />
                <span className="block text-xs text-slate-900 font-black uppercase tracking-wider mb-1">Monthly installment from</span>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl sm:text-5xl font-black text-emerald-700">€210</span>
                  <span className="text-sm text-slate-600 font-bold">/month incl. VAT</span>
                </div>
                <p className="text-xs text-slate-500 font-medium mt-2">Example based on 24 months installment plan</p>
              </div>

              <div className="pt-2">
                <a
                  href="#finansiering-formular"
                  className="rounded-xl bg-emerald-600 px-8 py-4 font-bold text-white shadow-lg transition hover:bg-emerald-700 inline-flex items-center gap-2"
                >
                  Inquire about financing
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* Right: Key Facts Card */}
            <div className="lg:col-span-5">
              <div className="rounded-3xl border-2 border-emerald-200 bg-white p-8 shadow-xl space-y-6">
                <h3 className="text-xl font-black text-slate-950">Key Details</h3>
                <ul className="space-y-4 text-sm font-semibold text-slate-800">
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                    <span>Split payments over up to 24 months</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                    <span>Instant digital credit evaluation</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                    <span>No cumbersome paperwork</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                    <span>Machine delivers directly to your stable</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Advantages */}
      <section className="py-20 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid gap-8 md:grid-cols-3">
            {anydayFordele.map((f, i) => (
              <div key={i} className="rounded-2xl border-2 border-emerald-100 bg-emerald-50/50 p-8 shadow-sm">
                <div className="mb-6">{f.icon}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{f.title}</h3>
                <p className="text-sm text-slate-700 font-medium leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step Guide */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-black text-slate-900 sm:text-4xl">4 Simple Steps to Your Danshaker</h2>
            <p className="mt-4 text-slate-700 font-semibold">Fast, transparent, and hassle-free</p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {trinGuide.map((t, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <span className="text-xs font-black text-emerald-600 tracking-widest block mb-2">{t.step}</span>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{t.title}</h3>
                <p className="text-xs text-slate-600 font-medium leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="finansiering-formular" className="py-24 bg-white scroll-mt-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <div className="text-center space-y-4 mb-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-1.5 text-xs font-bold text-emerald-800">
              <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Non-binding inquiry</span>
            </div>
            <h2 className="text-3xl font-black text-slate-900 sm:text-4xl">
              Inquire About Private Financing
            </h2>
            <p className="text-slate-600 text-base max-w-xl mx-auto">
              Fill out your details below and we will contact you with specific terms and installment options.
            </p>
          </div>

          <div className="rounded-3xl border-2 border-slate-200 bg-slate-50 p-6 md:p-10 shadow-xl">
            <ContactForm 
              source="Privatfinansiering" 
              defaultService="Privatfinansiering via AnyDay"
              buttonText="Send financing inquiry"
              lang="en"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
